import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { CompensationPageDocument } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import PolicyBalance from 'modules/attendance/components/SettingItemPage/TimeTracking/PolicyBalance'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import ExtraHoursRangeUtils from 'modules/attendance/lib/utils/ExtraHoursRangeUtils'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'

import CompensationConditions from './Content/CompensationConditions'
// trying this to make room for future modules belonging to either Conditions or Methods:
import CompensationMethods from './Content/CompensationMethods'
import NewOvertimeCompensation from './Content/NewOvertimeCompensation'
import OvertimeCompensation from './Content/OvertimeCompensation'
import WorkedTimeCategorization from './Content/WorkedTimeCategorization'

interface Props {
  attendanceSetting: AttendanceSetting
}

const Compensation = ({ attendanceSetting }: Props) => {
  const { company } = useCurrentSession()

  const { loading: loadingAttendanceFeatures, featureAvailability: timeoffCompensationEnabled } =
    useAttendanceFeatures(Features.timeoff_overtime_compensation)

  const { data, loading } = useQuery(CompensationPageDocument, {
    variables: {
      companyId: company.id,
      settingId: attendanceSetting.attributes.id,
      featureNames: [
        TimesheetFeatures.EXTRA_HOUR_RULES,
        TimesheetFeatures.BANK_OF_HOURS,
        TimesheetFeatures.SPECIAL_HOURS,
        TimesheetFeatures.BANK_OF_HOURS_TRANSFER_FREQUENCY,
        TimesheetFeatures.LIMIT_BALANCE_POOL,
        TimesheetFeatures.MANUAL_ALLOCATION,
        TimesheetFeatures.COMPLEMENTARY_HOURS
      ]
    }
  })

  const features = data?.features.featuresConnection.nodes ?? []
  const featureByName = groupBy(features, (feature) => feature.name)
  const _hasComplementaryHours = TimesheetFeatures.COMPLEMENTARY_HOURS in featureByName
  const hasExtraHourRules = TimesheetFeatures.EXTRA_HOUR_RULES in featureByName
  const hasLimitBalancePool = TimesheetFeatures.LIMIT_BALANCE_POOL in featureByName
  const hasBankOfHours = TimesheetFeatures.BANK_OF_HOURS in featureByName
  const hasSpecialHoursEnabled = TimesheetFeatures.SPECIAL_HOURS in featureByName
  const hasBankOfHoursTransferFrequency =
    TimesheetFeatures.BANK_OF_HOURS_TRANSFER_FREQUENCY in featureByName
  const hasManualAllocation = TimesheetFeatures.MANUAL_ALLOCATION in featureByName
  const extraHoursRanges = data?.attendance?.employeesSetting?.extrahoursrangeConnection.nodes ?? []
  const hasTimeOffExtraHourRules = ExtraHoursRangeUtils.hasTimeOffRanges(extraHoursRanges)
  const overtimePools = data?.attendance?.overtimePoolsConnection.nodes ?? []
  const graphqlBalanceCarryover = data?.attendance.employeesSetting?.balanceCarryover || undefined

  const employeesSettingId = attendanceSetting.attributes.id
  const balanceCarryoverId = attendanceSetting.attributes.balance_carryover_id

  const [balanceCarryover, setBalanceCarryover] = React.useState<BalanceCarryover>(
    new BalanceCarryover({
      id: graphqlBalanceCarryover?.id || balanceCarryoverId,
      attendance_employees_setting_id: employeesSettingId,
      reset_frequency: graphqlBalanceCarryover?.resetFrequency || 'monthly',
      frequency_number: graphqlBalanceCarryover?.frequencyNumber ?? 1
    })
  )

  React.useEffect(() => {
    if (graphqlBalanceCarryover) {
      setBalanceCarryover(
        new BalanceCarryover({
          id: graphqlBalanceCarryover.id || balanceCarryoverId,
          attendance_employees_setting_id: employeesSettingId,
          reset_frequency: graphqlBalanceCarryover.resetFrequency,
          frequency_number: graphqlBalanceCarryover.frequencyNumber ?? 1
        })
      )
    }
  }, [graphqlBalanceCarryover, employeesSettingId, balanceCarryoverId])

  if (loading || loadingAttendanceFeatures) {
    return (
      <Box padding="s40" width="full" alignItems="center">
        <Spinner />
      </Box>
    )
  }

  // Don't show CompensationConditions until they are approved.
  const showCompensationConditions = false

  return (
    <Box flexDirection="column">
      {hasBankOfHoursTransferFrequency ? (
        <NewOvertimeCompensation
          attendanceSetting={attendanceSetting}
          timeoffCompensationEnabled={timeoffCompensationEnabled}
          balanceCarryover={balanceCarryover}
          hasTimeOffExtraHourRules={hasTimeOffExtraHourRules}
          overtimePools={overtimePools}
          hasLimitBalancePool={hasLimitBalancePool}
          hasManualAllocation={hasManualAllocation}
          hasExtraHourRules={hasExtraHourRules}
        />
      ) : (
        <>
          <PolicyBalance
            attendanceSetting={attendanceSetting}
            balanceCarryover={balanceCarryover}
            setBalanceCarryover={setBalanceCarryover}
            hasBankOfHours={hasBankOfHours}
          />
          <OvertimeCompensation
            attendanceSetting={attendanceSetting}
            timeoffCompensationEnabled={timeoffCompensationEnabled}
            balanceCarryover={balanceCarryover}
            setBalanceCarryover={setBalanceCarryover}
            hasExtraHourRules={hasExtraHourRules}
            hasTimeOffExtraHourRules={hasTimeOffExtraHourRules}
            overtimePools={overtimePools}
            hasLimitBalancePool={hasLimitBalancePool}
          />
        </>
      )}
      {showCompensationConditions && (
        <CompensationConditions
          attendanceSetting={attendanceSetting}
          timeoffCompensationEnabled={timeoffCompensationEnabled}
          balanceCarryover={balanceCarryover}
          setBalanceCarryover={setBalanceCarryover}
          hasExtraHourRules={hasExtraHourRules}
          hasTimeOffExtraHourRules={hasTimeOffExtraHourRules}
          overtimePools={overtimePools}
          hasLimitBalancePool={hasLimitBalancePool}
          hasManualAllocation={hasManualAllocation}
          hasBankOfHoursTransferFrequency={hasBankOfHoursTransferFrequency}
        />
      )}
      <CompensationMethods
        balanceCarryover={graphqlBalanceCarryover}
        setBalanceCarryover={setBalanceCarryover}
        attendanceSetting={attendanceSetting}
        overtimePools={overtimePools}
        hasBankOfHoursTransferFrequency={hasBankOfHoursTransferFrequency}
      />
      {!hasSpecialHoursEnabled && (
        <WorkedTimeCategorization attendanceSetting={attendanceSetting} />
      )}
    </Box>
  )
}

export default observer(Compensation)
